import moment from 'moment';
import React from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Error';
import { ClassNameMap, makeStyles } from '@mui/styles';

import {
  AsyncTaskLeadStatus,
  AsyncTaskType,
  CareTeamRosterTaskStatus,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  AsyncTask,
  CandidateRosterTask,
  CareTeamRosterTask,
  PaginationType,
  PodShuffleAsyncTask,
} from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';

import {
  Button,
  Colors,
  IconButton,
  StatusIndicator,
  Table,
  Tooltip,
  UpdatedBy,
} from 'styleguide-v2';
import { Column } from 'styleguide-v2/src/components/Table';

import {
  downloadAsyncComparisonReport,
  downloadAsyncResult,
  downloadCTAsyncResult,
} from 'dash/src/services/AsyncServices';

const COMPARISON_REPORT_RELEASE_DATE = moment('2022-06-21');

function getCTStatus(status: CareTeamRosterTaskStatus) {
  if (status === CareTeamRosterTaskStatus.COMPLETED) {
    return <StatusIndicator status={status.toString()} color="success" />;
  }
  if (status === CareTeamRosterTaskStatus.IN_PROGRESS) {
    return <StatusIndicator status={status.toString()} color="loading" />;
  }
  if (status === CareTeamRosterTaskStatus.ERROR) {
    return <StatusIndicator status={status.toString()} color="error" />;
  }
  return status;
}

function getUploadStatus(upload: AsyncTask) {
  if (upload instanceof CareTeamRosterTask) return <></>;
  if (upload.error) {
    return <StatusIndicator status="Failed" color="error" />;
  }

  const warning = upload.warning ? (
    <Tooltip text={upload.warning}>
      <WarningIcon htmlColor={Colors.gold} />
    </Tooltip>
  ) : (
    <></>
  );

  return upload.result ? (
    <StatusIndicator
      color="success"
      status={
        upload.warning ? (
          <span className="flex gap">Completed{warning}</span>
        ) : (
          'Completed'
        )
      }
    />
  ) : (
    <StatusIndicator
      color="loading"
      status={
        <span className="flex gap">
          {`${upload.percentage.toFixed(0)}%`}
          {warning}
        </span>
      }
    />
  );
}

function getTbaStatus(status: string) {
  if (status === 'COMPLETE') {
    return <StatusIndicator status="Completed" color="success" />;
  }
  if (status === 'ERROR') {
    return <StatusIndicator status="Error" color="error" />;
  }
  if (status === 'N_A') {
    return translate('global.na');
  }
  return status;
}

function getLeadStatus(
  upload: AsyncTask,
  onReleaseToSF?: (upload: AsyncTask) => void,
) {
  if (upload.futureLeadsStatus === AsyncTaskLeadStatus.DEFAULT) {
    return (
      <StatusIndicator
        status={upload.futureLeadsStatus.toString()}
        color="closed"
      />
    );
  }
  if (upload.futureLeadsStatus === AsyncTaskLeadStatus.ON_HOLD) {
    return (
      <StatusIndicator
        status={upload.futureLeadsStatus.toString()}
        color="warning"
        subtitle={
          onReleaseToSF ? (
            <a
              style={{
                color: Colors.iconGreen,
                textDecoration: 'underline',
              }}
              onClick={(evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                onReleaseToSF(upload);
              }}
            >
              Release to Outreach
            </a>
          ) : undefined
        }
      />
    );
  }
  if (upload.futureLeadsStatus === AsyncTaskLeadStatus.RELEASED) {
    return (
      <StatusIndicator
        status={upload.futureLeadsStatus.toString()}
        color="success"
      />
    );
  }
  return upload.futureLeadsStatus;
}

type Props = {
  className?: string;
  onChangePage: (page: number) => void;
  onChangePageSize: (page: number) => void;
  onReleaseToSF?: (task: AsyncTask) => void;
  pagination?: PaginationType;
  pastUploads: (
    | AsyncTask
    | CareTeamRosterTask
    | CandidateRosterTask
    | PodShuffleAsyncTask
  )[];
  rosterType: AsyncTaskType;
};

const HISTORY_TABLE_COLUMNS = (
  { onReleaseToSF, rosterType }: Props,
  styles: ClassNameMap<'downloadButton'>,
) => {
  let columns: Column[] = [
    {
      headerName: translate('rosterIngestion.table.upload'),
      component: (
        upload:
          | AsyncTask
          | CareTeamRosterTask
          | CandidateRosterTask
          | PodShuffleAsyncTask,
      ) => (
        <UpdatedBy
          date={upload.createdAt}
          user={upload.createdBy.fullName || EMPTY}
        />
      ),
      width: 175,
    },
    {
      headerName: translate('rosterIngestion.table.rosterId'),
      component: (
        upload:
          | AsyncTask
          | CareTeamRosterTask
          | CandidateRosterTask
          | PodShuffleAsyncTask,
      ) => {
        if (upload instanceof AsyncTask) return upload.rosterId;
        if (upload instanceof CareTeamRosterTask) return upload.id;
        if (upload instanceof CandidateRosterTask) return upload.id;
        if (upload instanceof PodShuffleAsyncTask) return upload.id;
        return EMPTY;
      },
      width: 125,
    },
  ];

  if (rosterType === AsyncTaskType.MEMBER_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.referral'),
          field: 'referralOrgName',
        },
        {
          headerName: translate('rosterIngestion.table.records'),
          component: ({ total }: AsyncTask) => total - 1,
          width: 125,
        },
        {
          headerName: translate('rosterIngestion.table.ingestionStatus'),
          component: getUploadStatus,
          width: 175,
        },
      ],
    ];
  } else if (rosterType === AsyncTaskType.CANDIDATE_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.fileName'),
          component: ({ fileName }: CandidateRosterTask) => fileName,
        },
        {
          headerName: translate('rosterIngestion.table.records'),
          component: ({ numRows }: CandidateRosterTask) => numRows,
          width: 125,
        },
        {
          headerName: translate('rosterIngestion.table.ingestionStatus'),
          component: ({ status }: CandidateRosterTask) => getCTStatus(status),
          width: 175,
        },
      ],
    ];
  } else if (rosterType === AsyncTaskType.CARE_TEAM_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.description'),
          field: 'description',
        },
        {
          headerName: translate('rosterIngestion.table.records'),
          component: ({ numRows }: CareTeamRosterTask) => numRows,
          width: 125,
        },
        {
          headerName: translate('rosterIngestion.table.ingestionStatus'),
          component: ({ status }: CareTeamRosterTask) => getCTStatus(status),
          width: 175,
        },
      ],
    ];
  } else if (rosterType === AsyncTaskType.POD_SHUFFLE_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.records'),
          component: ({ numRows }: PodShuffleAsyncTask) => numRows,
          width: 125,
        },
        {
          headerName: translate('rosterIngestion.table.ingestionStatus'),
          component: ({ status }: PodShuffleAsyncTask) => {
            if (status === 'COMPLETED') {
              return <StatusIndicator status="Completed" color="success" />;
            }
            if (status === 'IN_PROGRESS') {
              return <StatusIndicator status="In Progress" color="loading" />;
            }
            if (status === 'ERROR') {
              return <StatusIndicator status="Error" color="error" />;
            }
            return status;
          },
          width: 175,
        },
        {
          headerName: translate('rosterIngestion.table.links'),
          component: (upload: PodShuffleAsyncTask) => {
            return (
              <div className="flex ">
                {upload.rawFileId ? (
                  <IconButton
                    onClick={() => {
                      downloadAsyncComparisonReport(
                        upload.rawFileId?.toString() || '',
                        `${upload.id}-raw-file.csv`,
                      );
                    }}
                    size="small"
                    tooltip={`${translate('global.download')} ${translate(
                      'rosterIngestion.table.rawFileLink',
                    )}`}
                  >
                    <DownloadIcon htmlColor={Colors.blue} />
                  </IconButton>
                ) : (
                  <div style={{ width: '3.3rem' }} />
                )}
                {upload.errorFileId ? (
                  <IconButton
                    onClick={() => {
                      downloadAsyncComparisonReport(
                        upload.errorFileId?.toString() || '',
                        `${upload.id}-error-report.csv`,
                      );
                    }}
                    size="small"
                    tooltip={`${translate('global.download')} ${translate(
                      'rosterIngestion.table.errorFileLink',
                    )}`}
                  >
                    <DownloadIcon htmlColor={Colors.error} />
                  </IconButton>
                ) : (
                  <div style={{ width: '3.3rem' }} />
                )}
              </div>
            );
          },
          width: 125,
        },
      ],
    ];
  }

  if (rosterType === AsyncTaskType.MEMBER_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.tbaStatus'),
          component: ({ tbaStatus }: AsyncTask) =>
            getTbaStatus(tbaStatus || ''),
          width: 175,
        },
        {
          headerName: translate('rosterIngestion.table.tbaStatusCount'),
          component: ({ tbaCount, tbaStatus }: AsyncTask) =>
            tbaStatus && tbaStatus !== 'N_A'
              ? tbaCount
              : translate('global.na'),
          width: 175,
        },
        {
          headerName: translate('rosterIngestion.table.leadStatus'),
          component: (upload: AsyncTask) =>
            getLeadStatus(upload, upload.rosterId ? onReleaseToSF : undefined),
          width: 125,
        },
      ],
    ];
  }

  if (
    ![
      AsyncTaskType.CANDIDATE_ROSTER,
      AsyncTaskType.POD_SHUFFLE_ROSTER,
    ].includes(rosterType)
  ) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.download'),
          component: (
            upload: AsyncTask | CareTeamRosterTask | PodShuffleAsyncTask,
          ) => {
            const showButton =
              // eslint-disable-next-line no-nested-ternary
              upload instanceof AsyncTask
                ? upload.result
                : upload instanceof CareTeamRosterTask
                ? upload.status === CareTeamRosterTaskStatus.COMPLETED
                : false;
            return showButton ? (
              <Button
                className={styles.downloadButton}
                color="secondary"
                icon={<DownloadIcon />}
                type="contained"
                onClick={() =>
                  // eslint-disable-next-line no-nested-ternary
                  upload instanceof AsyncTask
                    ? downloadAsyncResult(
                        upload.uuid,
                        `${upload.uuid}-results.csv`,
                      )
                    : upload instanceof CareTeamRosterTask
                    ? downloadCTAsyncResult(
                        upload.id,
                        `${upload.id}-results.csv`,
                      )
                    : undefined
                }
              />
            ) : (
              <></>
            );
          },
          width: 150,
        },
      ],
    ];
  }

  if (rosterType === AsyncTaskType.MEMBER_ROSTER) {
    columns = [
      ...columns,
      ...[
        {
          headerName: translate('rosterIngestion.table.comparisonReport'),
          component: (upload: AsyncTask) =>
            upload?.archiveMetadataId ? (
              <Button
                className={styles.downloadButton}
                color="secondary"
                icon={<DownloadIcon />}
                type="contained"
                onClick={() =>
                  downloadAsyncComparisonReport(
                    upload.archiveMetadataId || '',
                    `${upload.uuid}-comparison-report.csv`,
                  )
                }
              />
            ) : (
              <span>
                {moment
                  .unix(upload.createdAt)
                  .isAfter(COMPARISON_REPORT_RELEASE_DATE)
                  ? translate('rosterIngestion.inProgress')
                  : translate('rosterIngestion.notAvailable')}
              </span>
            ),
          width: 150,
        },
      ],
    ];
  }

  return columns;
};

const useStyles = makeStyles({
  downloadButton: {
    minWidth: '10rem',
  },
});

export const UploadHistoryTable = (props: Props) => {
  const styles = useStyles();
  const {
    className,
    onChangePage,
    onChangePageSize,
    pagination,
    pastUploads,
  } = props;

  return (
    <Table
      className={className}
      config={{
        columns: HISTORY_TABLE_COLUMNS(props, styles),
        data: pastUploads,
        pagination,
      }}
      empty
      fontSize="small"
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangePageSize}
      whiteBackground
    />
  );
};
